/**
 * GoogleMapsLinks - Displays "Open in Google Maps" button(s) for a route
 * 
 * For routes with ≤10 stops: Single button
 * For routes with >10 stops: Multiple buttons (Part 1, Part 2, etc.)
 */
import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Navigation, ExternalLink } from 'lucide-react';
import { splitRouteForGoogleMaps, isGoogleMapsDirectionsUrl } from '@/utils/googleMapsUrl';
import { useShopSettings } from '@/context/ShopSettingsContext';
import { selectShopRouteOrigin } from '@/utils/shopRouteOrigin';

const GoogleMapsLinks = ({ stops, className = '' }) => {
  const { routeOrigin } = useShopSettings() || {};
  const origin = selectShopRouteOrigin(routeOrigin);
  const segments = useMemo(() => {
    return splitRouteForGoogleMaps(stops, 10, origin.input);
  }, [stops, origin.input]);

  if (!segments || segments.length === 0) {
    return null;
  }

  const handleOpenMaps = (url) => {
    if (!isGoogleMapsDirectionsUrl(url)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Always reserve this line; changing acquisition state must not move controls.
  const originMessage = !origin.input
    ? origin.state === 'loading' ? 'Loading shop location…' : 'Shop location unavailable. Ask an administrator to check Shop Profile.'
    : origin.stale ? 'Using the previously loaded shop location.' : '';
  const originNotice = <p className="h-4 text-[10px] leading-4 text-stone-500 truncate" role="status"
    title={originMessage} data-testid="google-maps-origin-status">{originMessage}</p>;

  // Single segment - simple button
  if (segments.length === 1) {
    return (
      <div>
      <Button
        onClick={() => handleOpenMaps(segments[0].url)}
        disabled={!segments[0].url}
        title={!segments[0].url ? originMessage : undefined}
        className={`bg-blue-600 hover:bg-blue-700 text-white gap-2 ${className}`}
        data-testid="google-maps-link"
      >
        <Navigation className="h-4 w-4" />
        Open in Google Maps
        <ExternalLink className="h-3 w-3 opacity-70" />
      </Button>
      {originNotice}
      </div>
    );
  }

  // Multiple segments - show all links
  return (
    <div className={`space-y-2 ${className}`} data-testid="google-maps-links">
      <div className="flex items-center gap-2 text-xs text-stone-500 mb-1">
        <Navigation className="h-3.5 w-3.5" />
        <span>Open in Google Maps ({segments.length} parts)</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {segments.map((segment) => (
          <Button
            key={segment.segment}
            onClick={() => handleOpenMaps(segment.url)}
            disabled={!segment.url}
            title={!segment.url ? originMessage : undefined}
            variant="outline"
            size="sm"
            className="gap-1.5 text-blue-700 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
            data-testid={`google-maps-link-part-${segment.segment}`}
          >
            {segment.label}
            <ExternalLink className="h-3 w-3 opacity-70" />
          </Button>
        ))}
      </div>
      {originNotice}
      <p className="text-[10px] text-stone-400">
        Each part picks up where the last ended
      </p>
    </div>
  );
};

export default GoogleMapsLinks;
